import { PayloadAction, createSlice } from '@reduxjs/toolkit';

interface Allocator {
  id: string;
  allocatorName: string;
  city: string;
  state: string;
  pincode: string;
  vendorCode?: string;
}

export interface CreateAllocatorPayload {
  allocatorName: string;
  city?: string;
  state?: string;
  pincode?: string;
}

export interface AllocatorState {
  data: Allocator[] | null;
  loading: boolean;
  error: string | null;
  vendorCode: string | null;
  vendorCodeLoading: boolean;
  vendorCodeError: string | null;
  searchResults: Allocator[];
  searchLoading: boolean;
  createLoading: boolean;
  createError: string | null;
  lastCreatedAllocator: Allocator | null;
}

export const allocatorSliceState: AllocatorState = {
  data: null,
  loading: false,
  error: null,
  vendorCodeLoading: false,
  vendorCodeError: null,
  vendorCode: null,
  searchResults: [],
  searchLoading: false,
  createLoading: false,
  createError: null,
  lastCreatedAllocator: null,
};

const allocatorSlice = createSlice({
  name: 'allocator',
  initialState: allocatorSliceState,
  reducers: {
    fetchAllocatorsRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAllocatorsSuccess: (state, action: PayloadAction<Allocator[]>) => {
      state.data = action.payload;
      state.loading = false;
    },
    fetchAllocatorsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    fetchVendorCodeRequest: (state, action: PayloadAction<string>) => {
      state.vendorCodeLoading = true;
      state.vendorCodeError = null;
    },
    fetchVendorCodeSuccess: (
      state,
      action: PayloadAction<{ vendorCode: string }>
    ) => {
      const { vendorCode } = action.payload;
      state.vendorCode = vendorCode;
    },
    fetchVendorCodeFailure: (state, action: PayloadAction<string>) => {
      state.vendorCodeError = action.payload;
      state.vendorCodeLoading = false;
    },

    // Search allocators
    searchAllocatorsRequest: (state, action: PayloadAction<string>) => {
      state.searchLoading = true;
    },
    searchAllocatorsSuccess: (state, action: PayloadAction<Allocator[]>) => {
      state.searchResults = action.payload;
      state.searchLoading = false;
    },
    searchAllocatorsFailure: (state, action: PayloadAction<string>) => {
      state.searchLoading = false;
    },

    // Create allocator
    createAllocatorRequest: (
      state,
      action: PayloadAction<CreateAllocatorPayload>
    ) => {
      state.createLoading = true;
      state.createError = null;
    },
    createAllocatorSuccess: (state, action: PayloadAction<Allocator>) => {
      state.createLoading = false;
      state.lastCreatedAllocator = action.payload;
      if (state.data) {
        state.data = [action.payload, ...state.data];
      } else {
        state.data = [action.payload];
      }
    },
    createAllocatorFailure: (state, action: PayloadAction<string>) => {
      state.createLoading = false;
      state.createError = action.payload;
    },
    clearLastCreatedAllocator: (state) => {
      state.lastCreatedAllocator = null;
    },
  },
});

export const {
  fetchAllocatorsRequest,
  fetchAllocatorsSuccess,
  fetchAllocatorsFailure,
  fetchVendorCodeRequest,
  fetchVendorCodeSuccess,
  fetchVendorCodeFailure,
  searchAllocatorsRequest,
  searchAllocatorsSuccess,
  searchAllocatorsFailure,
  createAllocatorRequest,
  createAllocatorSuccess,
  createAllocatorFailure,
  clearLastCreatedAllocator,
} = allocatorSlice.actions;

export default allocatorSlice.reducer;
